/* eslint-disable no-param-reassign */
import { createSlice } from '@reduxjs/toolkit';
import jwt_decode from 'jwt-decode';

import {
    authApi, User, UserPermissions, usersAdminApi, workspacesAdminApi,
} from '@SERVICES';

import { GLOBAL_PERMISSIONS } from '@CONSTANTS/PERMISSIONS.constant';
import userRepository from '@REPOSITORIES/local-repository/user-repository';
import userPermissionsRepository from '@REPOSITORIES/local-repository/user-permissions-repository';

export type JwtPayload = {
    id: number;
    exp: number
    is_superuser: boolean;
    is_super_admin?: boolean;
    full_name: string;
    email: string;
    country: string;
    mobile_phone: string;
    office_phone: string;
    title: string;
    workspace_id?: number;
    workspace_name?: string;
    permissions: {
        id: number;

        name: string;

        codename: GLOBAL_PERMISSIONS,
    }[],
};

export type AuthState = {
    user: User | undefined;

    permissions: UserPermissions;
};

export function decodeUserToken(token: string | undefined) {
    if (!token) {
        return {
            user: undefined,

            permissions: {
                isSuperuser: false,

                global: [],
            },
        };
    }

    const decoded: JwtPayload = jwt_decode(token);

    return {
        user: {
            id: decoded.id,
            fullName: decoded.full_name,
            email: decoded.email,
            country: decoded.country,
            mobilePhone: decoded.mobile_phone,
            officePhone: decoded.office_phone,
            title: decoded.title,
            workspaceId: decoded.workspace_id,
            workspaceName: decoded.workspace_name,
        },
        permissions: {
            isSuperuser: decoded.is_superuser,
            global: decoded.permissions.map((permission) => ({
                id: permission.id,
                name: permission.name,
                codename: permission.codename,
            })),
        },
    };
}

const initialState: AuthState = {
    user: undefined,

    permissions: {
        isSuperuser: false,

        global: [],
    },
};

export const authSlice = createSlice({
    name: 'auth',
    initialState,
    reducers: {
        logout(state) {
            state.user = undefined;
            state.permissions = initialState.permissions;
        },
        switchWorkspace(state, action) {
            // Update user with new workspace information from the new token
            const decodedToken = decodeUserToken(action.payload.new_token);
            state.user = decodedToken.user;
            state.permissions = decodedToken.permissions;
        },
    },
    extraReducers: (builder) => {
        builder.addMatcher(
            authApi.endpoints.login.matchFulfilled,
            (state, action) => {
                const { token } = action.payload;

                const decodedToken = decodeUserToken(token);

                userRepository.setData(token);
                userPermissionsRepository.setData(decodedToken.permissions);

                state.user = decodedToken.user;
                state.permissions = decodedToken.permissions;
            },
        );

        builder.addMatcher(
            workspacesAdminApi.endpoints.switchWorkspace.matchFulfilled,
            (state, action) => {
                const { token, workspace } = action.payload;

                console.log('🔧 Auth reducer processing workspace switch...');
                console.log('📦 New token received (length):', token?.length);
                console.log('🏢 New workspace:', workspace);

                const decodedToken = decodeUserToken(token);

                console.log('🔍 Decoded token user:', decodedToken.user);

                // Update token in storage
                userRepository.setData(token);
                userPermissionsRepository.setData(decodedToken.permissions);

                // Update Redux state immediately
                state.user = decodedToken.user;
                state.permissions = decodedToken.permissions;

                console.log('✅ Auth state updated with new workspace');
            },
        );

        builder.addMatcher(
            usersAdminApi.endpoints.updatePermissions.matchFulfilled,
            (state, action) => {
                if (state.user) {
                    if (state.user.id === action.payload.id) {
                        const token = action.payload?.token;

                        const decodedToken = decodeUserToken(token);

                        userPermissionsRepository.setData(decodedToken.permissions);

                        state.permissions = decodedToken.permissions;
                    }
                }
            },
        );
    },
});
